import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// We manage scroll behaviour ourselves — prevent the browser from restoring its own position
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const SmoothScroll = ({ articleOpen = false }) => {
  const location = useLocation();

  // Initialise Lenis once
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.7,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  // Pause / resume Lenis when the article overlay is open so the article's own scroll works
  useEffect(() => {
    if (!window.__lenis) return;
    if (articleOpen) window.__lenis.stop();
    else window.__lenis.start();
  }, [articleOpen]);

  // Fallback: when landing directly on an article URL and closing it, smooth-scroll to Journal
  useEffect(() => {
    if (location.pathname !== '/') return;
    const target = location.state && location.state.scrollTo;
    if (!target) return;
    const smoothToTarget = () => {
      const el = document.getElementById(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20, duration: 1.2 });
      else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const t1 = setTimeout(smoothToTarget, 80);
    const t2 = setTimeout(smoothToTarget, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname, location.state]);

  return null;
};

export default SmoothScroll;
